import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, TextField, Button, Box,
  Link, Alert, CircularProgress, InputAdornment, IconButton,
  Checkbox, FormControlLabel
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { login, clearError } from '../../store/slices/authSlice';

const COLORS = {
  forestGreen: '#228B22',
  charcoal: '#36454F',
  lightGreen: '#32CD32',
  sage: '#9CAF88',
  cream: '#F5F5DC',
};

const validationSchema = yup.object({
  email: yup.string().email('Enter a valid email').required('Email is required'),
  password: yup.string().required('Password is required'),
});

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  
  const { isLoading, error, isAuthenticated, user } = useAppSelector((state) => state.auth);
  const [showPassword, setShowPassword] = useState(false);

  // Capture prefilled email from the Register success state
  const prefilledEmail = location.state?.email || '';

  useEffect(() => {
    // Redirect logic: Watch for successful login
    if (isAuthenticated && user) {
      const userType = user.user_type;
      let redirectPath = '/dashboard';

      if (userType === 'supplier' || userType === 'vendor') {
        redirectPath = '/solutions';
      } else if (userType === 'organization') {
        redirectPath = '/problems';
      }
      
      navigate(redirectPath, { replace: true });
    }
    
    return () => { if (error) dispatch(clearError()); };
  }, [isAuthenticated, user, navigate, dispatch, error]);

  const formik = useFormik({
    initialValues: {
      email: prefilledEmail,
      password: '',
      rememberMe: false,
    },
    enableReinitialize: true, 
    validationSchema,
    onSubmit: (values) => {
      dispatch(login({ email: values.email, password: values.password }));
    },
  });

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: 'rgba(255,255,255,0.8)',
      '& fieldset': { borderColor: `${COLORS.sage}50` },
      '&:hover fieldset': { borderColor: COLORS.forestGreen },
      '&.Mui-focused fieldset': { borderColor: COLORS.forestGreen, borderWidth: 2 },
    },
    '& .MuiInputLabel-root': { color: COLORS.charcoal, opacity: 0.7 },
    '& .MuiInputLabel-root.Mui-focused': { color: COLORS.forestGreen },
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: `linear-gradient(135deg, ${COLORS.cream} 0%, #e8f5e9 50%, ${COLORS.sage}40 100%)`,
      py: 4
    }}>
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{
          p: { xs: 4, md: 6 },
          borderRadius: 3,
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)',
          border: `1px solid ${COLORS.sage}30`,
          boxShadow: `0 25px 50px -12px ${COLORS.charcoal}20`,
        }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: COLORS.charcoal, mb: 1 }}>
              Welcome Back
            </Typography>
            <Typography variant="body1" sx={{ color: COLORS.charcoal, opacity: 0.7 }}>
              Login to manage your Rengigs account
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {typeof error === 'object' ? 'Invalid credentials. Please try again.' : error}
            </Alert>
          )}

          {location.state?.email && !error && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
              Account created! Please enter your password to continue.
            </Alert>
          )}

          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              id="email"
              name="email"
              label="Email Address"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && !!formik.errors.email}
              helperText={formik.touched.email && formik.errors.email}
              margin="normal"
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: COLORS.sage }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              id="password"
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && !!formik.errors.password}
              helperText={formik.touched.password && formik.errors.password}
              margin="normal"
              sx={inputSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: COLORS.sage }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox 
                    name="rememberMe" 
                    checked={formik.values.rememberMe} 
                    onChange={formik.handleChange}
                    sx={{ color: COLORS.forestGreen, '&.Mui-checked': { color: COLORS.forestGreen } }}
                  />
                }
                label={<Typography variant="body2">Remember me</Typography>}
              />
              <Link 
                component={RouterLink} 
                to="/forgot-password" 
                sx={{ color: COLORS.forestGreen, textDecoration: 'none', fontWeight: 600, fontSize: '0.875rem' }}
              >
                Forgot Password?
              </Link>
            </Box>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={isLoading}
              sx={{
                py: 1.5,
                borderRadius: 2,
                background: `linear-gradient(135deg, ${COLORS.forestGreen} 0%, ${COLORS.lightGreen} 100%)`,
                fontWeight: 700,
                fontSize: '1rem',
                textTransform: 'none',
                boxShadow: `0 10px 20px -5px ${COLORS.forestGreen}40`,
                '&:hover': {
                  background: COLORS.forestGreen,
                }
              }}
            >
              {isLoading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Sign In'}
            </Button>

            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="body2" sx={{ color: COLORS.charcoal, opacity: 0.8 }}>
                Don't have an account?{' '}
                <Link 
                  component={RouterLink} 
                  to="/register" 
                  sx={{ color: COLORS.forestGreen, fontWeight: 700, textDecoration: 'none' }}
                >
                  Create Account
                </Link>
              </Typography>
            </Box>
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;